<template>
  <div class="aqr-page">
    <!-- Header -->
    <div class="aqr-header">
      <!-- Row 1: title + search + refresh -->
      <div class="aqr-header-inner">
        <div class="aqr-title-group">
          <v-btn icon small class="back-btn" @click="$router.push('/questions')">
            <v-icon color="white">mdi-arrow-left</v-icon>
          </v-btn>
          <div>
            <div class="aqr-title">AI Images</div>
            <div v-if="lastUpdated" class="aqr-subtitle">
              Last updated: {{ lastUpdated }}
            </div>
          </div>
        </div>

        <div class="aqr-actions">
          <v-text-field
            v-model="searchQuery"
            placeholder="Search questions..."
            prepend-inner-icon="mdi-magnify"
            solo
            flat
            dense
            hide-details
            clearable
            class="aqr-search"
          ></v-text-field>
          <v-btn icon small @click="fetchData" :loading="loading" class="refresh-btn">
            <v-icon>mdi-refresh</v-icon>
          </v-btn>
        </div>
      </div>

      <!-- Row 2: API filters -->
      <div class="aqr-filters">
        <div class="aqr-filter-item">
          <v-icon size="15" color="rgba(255,255,255,0.7)" class="mr-1">mdi-account-outline</v-icon>
          <v-autocomplete
            v-model="filterName"
            :items="sidebarUsers"
            item-text="label"
            item-value="callerId"
            placeholder="All users"
            solo
            flat
            dense
            hide-details
            clearable
            no-data-text="No users found"
            class="aqr-filter-input"
            @change="fetchData"
            @click:clear="onNameCleared"
          ></v-autocomplete>
        </div>

        <div class="aqr-filter-item">
          <v-icon size="15" color="rgba(255,255,255,0.7)" class="mr-1">mdi-calendar-outline</v-icon>
          <v-select
            v-model="filterDateRange"
            :items="dateRangeOptions"
            item-text="label"
            item-value="value"
            solo
            flat
            dense
            hide-details
            class="aqr-filter-select"
            @change="fetchData"
          ></v-select>
        </div>

        <v-btn small class="aqr-apply-btn" @click="fetchData" :loading="loading">
          Apply
        </v-btn>
      </div>

      <div v-if="!loading" class="aqr-meta">
        {{ filteredItems.length }}
        {{ filteredItems.length === 1 ? "image" : "images" }}
        <template v-if="searchQuery && searchQuery.trim()">
          matching "{{ searchQuery }}"
        </template>
      </div>
    </div>

    <!-- Skeleton Loading -->
    <div v-if="loading" class="aqr-grid">
      <div
        v-for="n in 12"
        :key="'sk-' + n"
        class="aqr-card skeleton-card"
        :style="{ '--h': skeletonHeights[(n - 1) % skeletonHeights.length] }"
      >
        <div class="skeleton-body pulse"></div>
      </div>
    </div>

    <!-- Empty State -->
    <div v-else-if="filteredItems.length === 0" class="aqr-empty">
      <div class="aqr-empty-inner">
        <v-icon size="80" color="grey lighten-1">
          {{ searchQuery && searchQuery.trim() ? "mdi-magnify-close" : "mdi-image-off-outline" }}
        </v-icon>
        <div class="aqr-empty-title mt-3">
          {{ searchQuery && searchQuery.trim() ? "No matches found" : "No pictures found" }}
        </div>
        <div class="aqr-empty-text mt-2">
          {{
            searchQuery && searchQuery.trim()
              ? "Try a different search term."
              : "AI questions with images will appear here."
          }}
        </div>
        <v-btn
          v-if="searchQuery && searchQuery.trim()"
          class="mt-4"
          color="success"
          outline
          small
          @click="searchQuery = ''"
        >
          Clear search
        </v-btn>
      </div>
    </div>

    <!-- Masonry Gallery Grid -->
    <div v-else class="aqr-grid">
      <div
        v-for="(item, idx) in visibleItems"
        :key="getItemKey(item, idx)"
        class="aqr-card"
        @click="openLightbox(idx)"
      >
        <div class="aqr-card-inner">
          <img
            :src="getImageUrl(item)"
            :alt="item.question"
            class="aqr-img"
            loading="lazy"
            @error="onImgError($event)"
          />
          <div class="aqr-overlay">
            <!-- Download button top-right -->
            <v-btn
              icon
              small
              dark
              class="aqr-download-btn"
              @click.stop="downloadImage(getImageUrl(item))"
              title="Download"
            >
              <v-icon size="18">mdi-download</v-icon>
            </v-btn>

            <div class="aqr-overlay-body">
              <p class="aqr-overlay-question">{{ item.question }}</p>
              <div class="aqr-overlay-meta">
                <span class="aqr-overlay-chip">
                  <v-icon size="12" color="rgba(255,255,255,0.8)">mdi-calendar-outline</v-icon>
                  {{ formatDate(item.date) }}
                </span>
                <span v-if="item.name" class="aqr-overlay-chip">
                  <v-icon size="12" color="rgba(255,255,255,0.8)">mdi-account-outline</v-icon>
                  {{ item.name }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div v-if="hasMoreVisible" ref="scrollSentinel" class="aqr-scroll-sentinel">
        <v-progress-circular indeterminate size="24" width="2" color="success"></v-progress-circular>
      </div>
    </div>

    <!-- Lightbox -->
    <transition name="lightbox-fade">
      <div v-if="lightboxOpen" class="aqr-lightbox" @click.self="closeLightbox">
        <div class="aqr-lightbox-box">

          <v-btn
            icon
            dark
            class="lb-nav lb-prev"
            :disabled="lightboxIndex === 0"
            @click="prevImage"
          >
            <v-icon size="36">mdi-chevron-left</v-icon>
          </v-btn>

          <div class="lb-content" v-if="lightboxItem">
            <div class="lb-img-wrapper">
              <div class="lb-img-frame">
                <v-btn icon dark class="lb-close" @click="closeLightbox">
                  <v-icon>mdi-close</v-icon>
                </v-btn>
                <img
                  :src="getImageUrl(lightboxItem)"
                  :alt="lightboxItem.question"
                  class="lb-img"
                  @error="onImgError($event)"
                />
              </div>
            </div>
            <div class="lb-info">
              <p class="lb-question">{{ lightboxItem.question }}</p>
              <div class="lb-meta">
                <span class="lb-chip">
                  <v-icon size="14" class="mr-1" color="#9ca3af">mdi-calendar-outline</v-icon>
                  {{ formatDate(lightboxItem.date) }}
                </span>
                <span v-if="lightboxItem.name" class="lb-chip">
                  <v-icon size="14" class="mr-1" color="#9ca3af">mdi-account-outline</v-icon>
                  {{ lightboxItem.name }}
                </span>
              </div>
              <div class="lb-counter">{{ lightboxIndex + 1 }} / {{ visibleItems.length }}</div>
              <v-btn
                class="lb-download-btn mt-3"
                color="success"
                small
                @click="downloadImage(getImageUrl(lightboxItem))"
              >
                <v-icon small left>mdi-download</v-icon>
                Download
              </v-btn>
            </div>
          </div>

          <v-btn
            icon
            dark
            class="lb-nav lb-next"
            :disabled="lightboxIndex === visibleItems.length - 1"
            @click="nextImage"
          >
            <v-icon size="36">mdi-chevron-right</v-icon>
          </v-btn>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import moment from "moment";

export default {
  name: "AiImages",

  data() {
    return {
      items: [],
      sidebarUsers: [],
      searchQuery: "",
      filterName: "",
      filterDateRange: "30",
      batchSize: 20,
      visibleCount: 20,
      scrollObserver: null,
      dateRangeOptions: [
        { label: "Last 7 days",   value: "7"   },
        { label: "Last 30 days",  value: "30"  },
        { label: "Last 3 months", value: "90"  },
        { label: "Last year",     value: "365" },
        { label: "All time",      value: "all" },
      ],
      loading: true,
      lastUpdated: null,
      lightboxOpen: false,
      lightboxIndex: 0,
      skeletonHeights: ["220px", "300px", "180px", "260px", "320px", "200px"],
    };
  },

  computed: {
    filteredItems: function() {
      var q = this.searchQuery ? this.searchQuery.trim().toLowerCase() : "";
      if (!q) return this.items;
      return this.items.filter(function(item) {
        return (item.question || "").toLowerCase().indexOf(q) !== -1;
      });
    },

    visibleItems: function() {
      return this.filteredItems.slice(0, this.visibleCount);
    },

    hasMoreVisible: function() {
      return this.visibleItems.length < this.filteredItems.length;
    },

    lightboxItem: function() {
      return this.visibleItems[this.lightboxIndex] || null;
    },
  },

  mounted() {
    this.filterName = this.$store.state.phoneNumber || "";
    this.loadSidebarUsers();
    this.fetchData();
    window.addEventListener("keydown", this.onKeydown);
  },

  beforeDestroy() {
    window.removeEventListener("keydown", this.onKeydown);
    if (this.scrollObserver) {
      this.scrollObserver.disconnect();
      this.scrollObserver = null;
    }
    document.body.style.overflow = "";
  },

  watch: {
    searchQuery: function() {
      this.resetVisibleItems();
      this.refreshScrollObserver();
    },
  },

  methods: {
    getDomain() {
      return window.location.origin.startsWith("http://localhost")
        ? "https://localhost:5001"
        : "https://wercbergerproductions-app-164903695581.us-east1.run.app";
    },

    async loadSidebarUsers() {
      try {
        var response = await this.$http.get(
          this.getDomain() + "/api/openai/getChatSidebar"
        );
        var data = Array.isArray(response.data) ? response.data : [];
        var vm = this;
        this.sidebarUsers = data.map(function(chat) {
          return {
            label: chat.name
              ? chat.name + " " + vm.formatCallerId(chat.callerId)
              : vm.formatCallerId(chat.callerId),
            callerId: chat.callerId,
          };
        });
      } catch (e) {
        console.error("AiImages: failed to load sidebar users", e);
      }
    },

    onNameCleared() {
      this.filterName = "";
      this.fetchData();
    },

    resetVisibleItems() {
      this.visibleCount = this.batchSize;
      this.lightboxIndex = 0;
    },

    loadMoreItems() {
      if (this.visibleCount >= this.filteredItems.length) return;
      this.visibleCount += this.batchSize;
    },

    setupScrollObserver() {
      if (this.scrollObserver) {
        this.scrollObserver.disconnect();
        this.scrollObserver = null;
      }

      if (!("IntersectionObserver" in window)) return;
      var sentinel = this.$refs.scrollSentinel;
      if (!sentinel) return;

      var vm = this;
      this.scrollObserver = new IntersectionObserver(
        function(entries) {
          entries.forEach(function(entry) {
            if (entry.isIntersecting) {
              vm.loadMoreItems();
            }
          });
        },
        {
          root: null,
          rootMargin: "220px 0px 220px 0px",
          threshold: 0,
        }
      );

      this.scrollObserver.observe(sentinel);
    },

    refreshScrollObserver() {
      var vm = this;
      this.$nextTick(function() {
        vm.setupScrollObserver();
      });
    },

    async fetchData() {
      this.loading = true;
      try {
        var fromDate = this.filterDateRange === "all"
          ? "2020-01-01"
          : moment().subtract(parseInt(this.filterDateRange), "days").format("YYYY-MM-DD");

        var params = { fromDate: fromDate };
        if (this.filterName && this.filterName.trim()) {
          params.name = this.filterName.trim();
        }

        var response = await this.$http.get(
          this.getDomain() + "/api/openai/getAiQuestionsWithPictures",
          { params: params }
        );

        var raw = response.data;
        var data = Array.isArray(raw) ? raw : (raw && (raw.items || raw.data)) || [];

        this.items = data
          .filter(function(item) {
            return !!this.getImageUrl(item);
          }, this)
          .sort(function(a, b) {
            return (
              new Date(b.date || 0) - new Date(a.date || 0)
            );
          });

        this.resetVisibleItems();
        this.lastUpdated = moment().format("MMM D, h:mm A");
      } catch (e) {
        console.error("AiImages fetch error:", e);
        this.items = [];
        this.resetVisibleItems();
      } finally {
        this.loading = false;
        this.refreshScrollObserver();
      }
    },

    getImageUrl(item) {
      return item.urls || item.pictureUrl || item.imageUrl || item.picture || null;
    },

    getItemKey(item, idx) {
      return item.id || item.questionId || (idx + "-" + item.date);
    },

    formatDate(dateStr) {
      if (!dateStr) return "";
      return moment(dateStr).format("MMM D, YYYY");
    },

    formatCallerId(callerId) {
      if (!callerId) return "";
      var value = ("" + callerId).replace(/\D/g, "");
      if (value.length === 11) {
        value = value.substring(1);
      }
      var match = value.match(/^(\d{3})(\d{3})(\d{4})$/);
      if (match) {
        return "(" + match[1] + ") " + match[2] + "-" + match[3];
      }
      return callerId;
    },

    onImgError(e) {
      e.target.style.visibility = "hidden";
    },

    downloadImage(url) {
      if (!url) return;
      var fileNameFromUrl = url.split("/").pop() || "ai-image.jpg";
      var cleanFileName = fileNameFromUrl.split("?")[0] || "ai-image.jpg";
      var proxyUrl =
        this.getDomain() +
        "/api/openAi/downloadFile?url=" +
        encodeURIComponent(url) +
        "&fileName=" +
        encodeURIComponent(cleanFileName);

      var link = document.createElement("a");
      link.href = proxyUrl;
      link.setAttribute("download", cleanFileName);
      link.rel = "noopener noreferrer";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },

    openLightbox(idx) {
      this.lightboxIndex = idx;
      this.lightboxOpen = true;
      document.body.style.overflow = "hidden";
    },

    closeLightbox() {
      this.lightboxOpen = false;
      document.body.style.overflow = "";
    },

    prevImage() {
      if (this.lightboxIndex > 0) this.lightboxIndex--;
    },

    nextImage() {
      if (this.lightboxIndex < this.visibleItems.length - 1)
        this.lightboxIndex++;
    },

    onKeydown(e) {
      if (!this.lightboxOpen) return;
      if (e.key === "ArrowLeft") this.prevImage();
      else if (e.key === "ArrowRight") this.nextImage();
      else if (e.key === "Escape") this.closeLightbox();
    },
  },
};
</script>

<style scoped>
/* ─── Page ────────────────────────────────────────────────── */
.aqr-page {
  min-height: 100vh;
  background-color: #f0f2f5;
  font-family: "Roboto", sans-serif;
}

/* ─── Header — green gradient matching AI Questions ───────── */
.aqr-header {
  background: linear-gradient(135deg, #4caf50 0%, #45a049 100%);
  padding: 12px 16px 14px;
  color: white;
}

.aqr-header-inner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 4px;
}

.aqr-title-group {
  display: flex;
  align-items: center;
  gap: 8px;
}

.back-btn {
  margin-right: 4px;
}

.aqr-title {
  font-size: 20px;
  font-weight: 500;
  color: white;
  line-height: 1.2;
}

.aqr-subtitle {
  font-size: 11px;
  color: rgba(255, 255, 255, 0.8);
  margin-top: 2px;
}

.aqr-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  justify-content: flex-end;
  max-width: 380px;
}

.aqr-search {
  border-radius: 8px;
  flex: 1;
}

.aqr-search >>> .v-input__slot {
  background-color: rgba(255, 255, 255, 0.95) !important;
  border-radius: 8px !important;
  box-shadow: none !important;
}

.aqr-search >>> .v-input__control {
  min-height: 42px !important;
}

.aqr-search >>> input {
  font-size: 14px;
}

.refresh-btn {
  color: white !important;
  flex-shrink: 0;
}

/* ─── Filter bar ──────────────────────────────────────────── */
.aqr-filters {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 2px;
  flex-wrap: wrap;
}

.aqr-filter-item {
  display: flex;
  align-items: center;
  background-color: rgba(255, 255, 255, 0.95);
  border-radius: 8px;
  padding: 0 4px 0 8px;
  flex: 1;
  min-width: 160px;
  max-width: 260px;
}

.aqr-filter-item > .v-icon {
  color: #666 !important;
  flex-shrink: 0;
}

.aqr-filter-input,
.aqr-filter-select {
  background: transparent !important;
}

.aqr-filter-input >>> .v-input__slot,
.aqr-filter-select >>> .v-input__slot {
  background: transparent !important;
  box-shadow: none !important;
  padding: 0 4px !important;
}

.aqr-filter-input >>> input,
.aqr-filter-select >>> .v-select__selection {
  color: #333 !important;
  font-size: 13px;
}

.aqr-filter-input >>> input::placeholder {
  color: #999 !important;
}

.aqr-filter-input >>> .v-input__control,
.aqr-filter-select >>> .v-input__control {
  min-height: 38px !important;
}

.aqr-filter-input >>> .v-icon,
.aqr-filter-select >>> .v-icon {
  color: #666 !important;
}

.aqr-filter-select >>> .v-select__selections {
  padding: 0 !important;
}

.aqr-apply-btn {
  background-color: #ffffff !important;
  color: #2e7d32 !important;
  font-weight: 700;
  text-transform: none;
  letter-spacing: 0;
  border: 1px solid rgba(255, 255, 255, 0.95);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  flex-shrink: 0;
  min-width: 72px;
}

.aqr-apply-btn:hover {
  background-color: #f1f8f1 !important;
  color: #1b5e20 !important;
}

.aqr-meta {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.8);
  margin-top: 8px;
}

/* ─── Masonry Grid ────────────────────────────────────────── */
.aqr-grid {
  columns: 4 260px;
  column-gap: 16px;
  padding: 20px 20px 40px;
}

.aqr-scroll-sentinel {
  break-inside: avoid;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 52px;
}

/* ─── Gallery Card ────────────────────────────────────────── */
.aqr-card {
  break-inside: avoid;
  margin-bottom: 16px;
  border-radius: 12px;
  overflow: hidden;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: transform 0.25s ease, box-shadow 0.25s ease;
}

.aqr-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.18);
}

.aqr-card:hover .aqr-overlay {
  opacity: 1;
}

.aqr-card:hover .aqr-img {
  transform: scale(1.04);
}

.aqr-card-inner {
  position: relative;
  width: 100%;
  background: #e5e7eb;
}

.aqr-img {
  width: 100%;
  height: auto;
  display: block;
  transition: transform 0.4s ease;
}

/* Overlay */
.aqr-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(
    to top,
    rgba(0, 0, 0, 0.82) 0%,
    rgba(0, 0, 0, 0.35) 45%,
    rgba(0, 0, 0, 0) 100%
  );
  opacity: 0;
  transition: opacity 0.28s ease;
  display: flex;
  align-items: flex-end;
}

.aqr-download-btn {
  position: absolute !important;
  top: 8px;
  right: 8px;
  background-color: rgba(0, 0, 0, 0.45) !important;
  backdrop-filter: blur(4px);
  transition: background-color 0.2s;
}

.aqr-download-btn:hover {
  background-color: rgba(76, 175, 80, 0.8) !important;
}

.aqr-overlay-body {
  padding: 14px;
  width: 100%;
}

.aqr-overlay-question {
  color: #fff;
  font-size: 13px;
  font-weight: 500;
  line-height: 1.5;
  margin: 0 0 8px;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.aqr-overlay-meta {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.aqr-overlay-chip {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  font-size: 11px;
  color: rgba(255, 255, 255, 0.8);
}

/* ─── Skeleton ────────────────────────────────────────────── */
.skeleton-card {
  cursor: default;
}

.skeleton-card:hover {
  transform: none;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.skeleton-body {
  width: 100%;
  height: var(--h, 220px);
  background: #e0e0e0;
  border-radius: 12px;
}

@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.45; }
}

.pulse {
  animation: pulse 1.6s ease-in-out infinite;
}

/* ─── Empty State ─────────────────────────────────────────── */
.aqr-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 60vh;
  padding: 40px 20px;
}

.aqr-empty-inner {
  text-align: center;
  color: #667781;
}

.aqr-empty-title {
  font-size: 18px;
  font-weight: 500;
  color: #333;
}

.aqr-empty-text {
  font-size: 14px;
  color: #667781;
}

/* ─── Lightbox ────────────────────────────────────────────── */
.aqr-lightbox {
  position: fixed;
  inset: 0;
  z-index: 9999;
  background: rgba(0, 0, 0, 0.92);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px 14px;
  box-sizing: border-box;
}

.aqr-lightbox-box {
  position: relative;
  display: flex;
  align-items: center;
  gap: 8px;
  max-width: 96vw;
  max-height: 96vh;
  width: 100%;
}

.lb-close {
  position: absolute !important;
  top: 10px;
  right: 10px;
  z-index: 3;
  background-color: rgba(0, 0, 0, 0.45) !important;
  backdrop-filter: blur(4px);
}

.lb-nav {
  flex-shrink: 0;
}

.lb-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  min-width: 0;
  max-width: calc(100vw - 130px);
}

.lb-img-wrapper {
  width: min(100%, calc(100vw - 140px));
  max-height: calc(100vh - 220px);
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 12px;
  overflow: hidden;
  background: #111;
}

.lb-img-frame {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  max-width: 100%;
  max-height: calc(100vh - 220px);
}

.lb-img {
  max-width: 100%;
  max-height: calc(100vh - 220px);
  object-fit: contain;
  display: block;
  border-radius: 12px;
}

.lb-info {
  text-align: center;
  max-width: 900px;
  width: 100%;
  padding-bottom: 4px;
}

.lb-question {
  color: #f9fafb;
  font-size: 15px;
  font-weight: 500;
  line-height: 1.6;
  margin: 0 0 10px;
}

.lb-meta {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  flex-wrap: wrap;
}

.lb-chip {
  display: inline-flex;
  align-items: center;
  font-size: 13px;
  color: #9ca3af;
}

.lb-counter {
  margin-top: 8px;
  font-size: 12px;
  color: #6b7280;
}

.lb-download-btn {
  text-transform: none !important;
  letter-spacing: 0 !important;
}

/* ─── Lightbox transition ─────────────────────────────────── */
.lightbox-fade-enter-active,
.lightbox-fade-leave-active {
  transition: opacity 0.22s ease;
}
.lightbox-fade-enter,
.lightbox-fade-leave-to {
  opacity: 0;
}

/* ─── Responsive ──────────────────────────────────────────── */
@media (max-width: 900px) {
  .aqr-grid {
    columns: 2 160px;
    padding: 16px 12px 32px;
  }

  .aqr-actions {
    max-width: 100%;
    width: 100%;
  }
}

@media (max-width: 480px) {
  .aqr-grid {
    columns: 1;
    padding: 12px 10px 24px;
  }

  .lb-nav {
    display: none;
  }

  .lb-content {
    max-width: calc(100vw - 24px);
  }

  .lb-img-wrapper {
    width: calc(100vw - 24px);
    max-height: calc(100vh - 240px);
  }

  .lb-img {
    max-height: calc(100vh - 240px);
  }
}
</style>
